import { Inject, Injectable } from '@angular/core';

import { EventOption, EventOptionGroup } from '@frontend/sports/betting-offer/feature/model';
import { SourceOfPick } from '@frontend/sports/option/pick/feature/tracking';
import { Decimal } from 'decimal.js';
import { Observable, map } from 'rxjs';

import { CurrencyPipe } from '../../../base/pipes/currency.pipe';
import { BETSLIP_MESSAGES_PROVIDER_INJECTION_TOKEN, IBetslipMessagesProviderService } from '../../../base/services/betslip-messages-provider.service';
import { IBetslipState } from '../../../base/store/state';
import { BetslipType } from '../../../core/betslip-type';
import { BetslipPick } from '../../../core/picks/betslip-pick';
import { BetslipV1Pick } from '../../../core/picks/betslip-v1-pick';
import { BetslipV2OptionMarketPick } from '../../../core/picks/betslip-v2-option-market-pick';
import { PickId, V1PickId, V2OptionMarketPickId } from '../../../core/picks/pick-id';
import { IDigitalBetslipMessages } from '../../../digital/services/betslip-message-provider.service';
import { BetslipGeneratorContextType } from '../../bet-generation/models';
import { BetslipGeneratorInputBuilder } from '../../bet-generation/services/betslip-generator-input-builder.service';
import { BetslipGeneratorService } from '../../bet-generation/services/betslip-generator.service';
import BetslipSummaryBuilder from '../../summary/services/betslip-summary-builder.service';
import { IncorrectOptionsCountPreCheckError } from '../../validation/errors/pre-check/incorrect-options-count-pre-check-error';
import { ISportPick } from '../models';
import { ILineSwitcherPossibleWinningsState } from '../state';
import LineSwitcherInputBuilderService from './line-switcher-input-builder';

@Injectable()
export class LineSwitcherUtils {
    messages: IDigitalBetslipMessages;

    constructor(
        private betslipGenerator: BetslipGeneratorService,
        private betslipGeneratorInputBuilder: BetslipGeneratorInputBuilder,
        private lineSwitcherInputBuilder: LineSwitcherInputBuilderService,
        private betslipSummaryBuilder: BetslipSummaryBuilder,
        private currencyPipe: CurrencyPipe,
        @Inject(BETSLIP_MESSAGES_PROVIDER_INJECTION_TOKEN) messageService: IBetslipMessagesProviderService,
    ) {
        this.messages = messageService.getMessages() as IDigitalBetslipMessages;
    }

    getPossibleWinnings(betslipState: IBetslipState): Observable<ILineSwitcherPossibleWinningsState> {
        const filteredErrorsState = this.filterIncorrectOptionsCountErrors(betslipState);

        const lineSwitcherPickId = filteredErrorsState.lineSwitcher.swapPickState.oldPickId;
        const calculatorInput = this.lineSwitcherInputBuilder.getCalculatorInput(filteredErrorsState, lineSwitcherPickId);
        const restrictions = this.betslipGeneratorInputBuilder.getRestrictions(BetslipGeneratorContextType.BetslipLineSwitcher, filteredErrorsState);
        const overrides = this.lineSwitcherInputBuilder.getOverrides(filteredErrorsState);

        const betslip = this.betslipGenerator.calculate(calculatorInput, { overrides, restrictions });

        return this.betslipSummaryBuilder.createBetslipSummary(betslip).pipe(
            map((summary) => {
                const hasError = betslip.slips.some((x) => x.hasError);
                const originalWinNet = hasError ? null : summary.possibleWinnings?.minus(summary.taxation?.amount || new Decimal(0));

                const accaBoostModifications = summary.slipSummaries.length === 1 && summary.slipSummaries[0].rewardTokenModifications?.accaBoost;
                const isBoosted = !!accaBoostModifications;

                const possibleWinningsTitle = summary.taxation
                    ? isBoosted
                        ? this.messages.lineSwitcher.BoostedWinningsNet
                        : this.messages.lineSwitcher.PossibleWinningsNet
                    : isBoosted
                      ? this.messages.lineSwitcher.BoostedWinnings
                      : this.messages.lineSwitcher.PossibleWinnings;

                return {
                    title: possibleWinningsTitle,
                    winnings: this.currencyPipe.transform(isBoosted ? accaBoostModifications.originalWinnings : originalWinNet, true),
                    boostedWinnings: isBoosted ? this.currencyPipe.transform(accaBoostModifications.boostedWinnings, true) : null,
                };
            }),
        );
    }

    static getBetDetails(optionGroup: EventOptionGroup, option: EventOption) {
        const betDetails: string[] | undefined[] = [];
        if (optionGroup.detailedGrouping?.marketTabId && optionGroup.detailedGrouping?.optionColumnId) {
            betDetails[1] = optionGroup.periodName;
            betDetails[2] = option.optionColumnName;
        } else {
            betDetails[1] = optionGroup.name;
        }
        betDetails[0] = SourceOfPick.LineSwitcher.toString();

        return betDetails.join(';');
    }

    static getPick(pickId: PickId, betslipState: IBetslipState): BetslipPick {
        return betslipState.types.base.currentSelectedType === BetslipType.EditBet
            ? betslipState.types.editBet.picks.pickList.find((p) => p.id.isEqual(pickId))!
            : betslipState.picks.pickList.find((p) => p.id.isEqual(pickId))!;
    }

    static getOption(optionId: number, options: EventOption[]): EventOption | undefined {
        return options.find((o) => o.id === optionId);
    }

    static convertToSportPick(pick: BetslipV1Pick | BetslipV2OptionMarketPick): ISportPick {
        return BetslipV1Pick.isPick(pick)
            ? {
                  id: pick.id.toString(),
                  optionId: pick.option.id,
                  eventId: pick.event.id,
                  optionGroupId: pick.market.id,
                  marketTemplateId: pick.market.templateId,
                  price: pick.option.price,
              }
            : {
                  id: pick.id.toString(),
                  optionId: pick.option.id,
                  eventId: pick.fixture.fixtureId,
                  optionGroupId: pick.market.id,
                  marketTemplateId: -1,
                  price: pick.option.prices[0],
              };
    }

    static isSportsPick(pickId: PickId): pickId is V1PickId | V2OptionMarketPickId {
        return V1PickId.isId(pickId) || V2OptionMarketPickId.isId(pickId);
    }

    private filterIncorrectOptionsCountErrors(betslipState: IBetslipState): IBetslipState {
        return {
            ...betslipState,
            errors: {
                ...betslipState.errors,
                betslipErrors: betslipState.errors.betslipErrors.filter((error) => !(error instanceof IncorrectOptionsCountPreCheckError)),
            },
        };
    }
}
